import { useEffect, useMemo, useRef, useState } from "react";

// ─── As-of date picker ──────────────────────────────────────────────────────────
// Mirrors the global DateRangePicker's trigger + calendar look so the WI nav-bar
// control reads as the same component family. Data flow is unchanged: options come
// from snapshotDates (available historical snapshots), selecting a date calls
// onChange(dateStr), "Today" calls onChange(null). Dates not in snapshotDates are
// rendered disabled (greyed, not clickable), matching how the global picker greys
// out future dates. Helpers (MONTHS/DAYS/formatting) are re-defined locally to keep
// this file self-contained and decoupled from DateRangePicker.js.

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const pad2 = (n) => String(n).padStart(2, "0");
const toDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromDateStr = (s) => (s ? new Date(s + "T00:00:00") : null);

/** "Aug 25, 2026" — matches DateRangePicker's fmtDisplay (no weekday). */
const fmtDisplay = (s) => {
  if (!s) return "";
  const d = fromDateStr(s);
  if (!d || Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const AsOfDatePicker = ({ snapshotDates, asOfDate, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Available historical dates, as a Set for O(1) lookup. snapshotDates arrives
  // newest-first from the API; we don't need ordering for a calendar grid.
  const available = useMemo(() => {
    if (!snapshotDates || snapshotDates.length === 0) return null;
    return new Set(snapshotDates);
  }, [snapshotDates]);
  const hasAvailable = Boolean(available);

  // Calendar view state. Default to the selected date's month, else today.
  const todayStr = toDateStr(new Date());
  const focusSeed = asOfDate ? fromDateStr(asOfDate) : new Date();
  const [viewYear, setViewYear] = useState(focusSeed.getFullYear());
  const [viewMonth, setViewMonth] = useState(focusSeed.getMonth());

  // Sync the view to the selection whenever the dropdown opens, so reopening
  // after picking a far-past snapshot jumps back to that month.
  useEffect(() => {
    if (!isOpen) return;
    const seed = asOfDate ? fromDateStr(asOfDate) : new Date();
    setViewYear(seed.getFullYear());
    setViewMonth(seed.getMonth());
  }, [isOpen, asOfDate]);

  // Close on outside click. Mirrors DateRangePicker's pattern.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const navigateMonth = (dir) => {
    let m = viewMonth + dir;
    let y = viewYear;
    if (m > 11) { m = 0; y++; }
    if (m < 0) { m = 11; y--; }
    setViewMonth(m);
    setViewYear(y);
  };

  const choose = (value) => { onChange(value); setIsOpen(false); };
  const onTriggerKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsOpen((o) => !o); }
  };
  const onPanelKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); setIsOpen(false); }
  };

  // Build the 6-row day grid for the current view month (42 cells = Sun..Sat).
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length < 42) cells.push(null);

  const triggerLabel = asOfDate ? fmtDisplay(asOfDate) : "Today";

  // When there are no historical snapshots AND nothing is selected, the only
  // meaningful state is "Today" (the default) — so the picker offers nothing
  // to change. Match the prior component's empty-state guard.
  if (!hasAvailable && !asOfDate) return null;

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger button — same class string + icon as global DateRangePicker */}
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={`inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm outline-none focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent ${isOpen ? "ring-2 ring-[#32B1FF] border-transparent" : ""}`}
      >
        <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <span className="flex items-baseline gap-1.5 min-w-0 text-sm">
          <span className="font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">{triggerLabel}</span>
        </span>
        <svg className="w-3 h-3 text-gray-400 ml-auto" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Select an as-of date"
          tabIndex={-1}
          onKeyDown={onPanelKeyDown}
          className="absolute top-full mt-2 right-0 z-50 w-[280px] max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-5 flex flex-col gap-3 outline-none"
        >
          {/* Month navigation header — matches DateRangePicker's arrows + label */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              aria-label="Previous month"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              aria-label="Next month"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day-of-week header row */}
          <div className="grid grid-cols-7 gap-0 mb-1">
            {DAYS.map((d, i) => (
              <div key={i} className="text-center text-[11px] font-medium text-gray-400 dark:text-gray-500 py-1">{d}</div>
            ))}
          </div>

          {/* Day grid (always 42 cells = 6 rows) */}
          <div className="grid grid-cols-7 gap-0">
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} className="h-8" />;
              const dateStr = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`;
              const isSelected = dateStr === asOfDate;
              const isToday = dateStr === todayStr;
              const isFuture = dateStr > todayStr;
              const isSelectable = !isFuture && available && available.has(dateStr);

              return (
                <div
                  key={i}
                  onClick={isSelectable ? () => choose(dateStr) : undefined}
                  className={`h-8 flex items-center justify-center text-sm transition-colors relative
                    ${isSelected
                      ? "bg-[#32B1FF] text-white font-semibold rounded-md z-10"
                      : isSelectable
                      ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                      : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                    }
                    ${isToday && !isSelected ? "text-[#32B1FF] font-semibold" : ""}
                  `}
                >
                  {day}
                </div>
              );
            })}
          </div>

          {/* Today quick-select — resets asOfDate to null (the live view) */}
          <button
            type="button"
            onClick={() => choose(null)}
            className="mt-1 w-full px-3 py-2 text-sm font-medium rounded-lg bg-[#32B1FF] text-white hover:bg-blue-600 transition-colors"
          >
            Today
          </button>
        </div>
      )}
    </div>
  );
};

export default AsOfDatePicker;
